/**
 * The standard record table.
 *
 * Lifted from the pattern the One Up Client portal already had right, which was
 * the cleanest table in the codebase: the whole row is the click target, the
 * type-identity and status live in badges rather than bare text, values are top
 * aligned so a two-line cell does not centre itself against single-line
 * neighbours, and the row ends with one explicit Open control for anyone who
 * does not realise the row itself is clickable.
 *
 * Cascade had its own `th`/`td` class constants per view; the columns differed
 * in padding and the rows in hover treatment. This replaces them so a table is
 * a table everywhere.
 */

import type { ReactNode } from 'react';
import { Card } from '../ui/card';
import { cn } from '../lib/utils';

export interface RecordTableColumn {
  key: string;
  header: ReactNode;
  /** Right-align — for the trailing action column and for counts. */
  align?: 'left' | 'right';
  /** Suppress below `sm`, for columns that are context rather than identity. */
  hideOnMobile?: boolean;
  className?: string;
}

export function RecordTable({
  columns,
  minWidth = 820,
  children,
}: {
  columns: RecordTableColumn[];
  /** Below this the table scrolls horizontally rather than crushing columns. */
  minWidth?: number;
  children: ReactNode;
}) {
  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm" style={{ minWidth }}>
          <thead>
            <tr className="border-b border-border text-left text-[10px] font-semibold uppercase tracking-[0.07em] text-muted-foreground">
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className={cn(
                    'whitespace-nowrap px-3 py-2.5 font-semibold first:pl-4 last:pr-4',
                    column.align === 'right' && 'text-right',
                    column.hideOnMobile && 'hidden sm:table-cell',
                    column.className,
                  )}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>{children}</tbody>
        </table>
      </div>
    </Card>
  );
}

/**
 * A clickable row. `onOpen` is what the row does; nested links and buttons must
 * stop propagation themselves so a phone number stays dialable without also
 * opening the record.
 */
export function RecordTableRow({
  onOpen,
  selected,
  flagged,
  children,
}: {
  onOpen?: () => void;
  selected?: boolean;
  /** Needs attention — a lead in the loop, an entry awaiting triage. */
  flagged?: boolean;
  children: ReactNode;
}) {
  return (
    <tr
      onClick={onOpen}
      className={cn(
        'border-b border-border align-top transition-colors last:border-0',
        onOpen && 'cursor-pointer',
        selected ? 'bg-selection shadow-[inset_3px_0_0_hsl(var(--primary))]' : flagged ? 'bg-selection hover:bg-selection' : 'hover:bg-muted/40',
      )}
    >
      {children}
    </tr>
  );
}

export function RecordTableCell({
  align,
  hideOnMobile,
  className,
  children,
}: {
  align?: 'left' | 'right';
  hideOnMobile?: boolean;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <td
      className={cn(
        'px-3 py-3 text-[13px] text-foreground first:pl-4 last:pr-4',
        align === 'right' && 'text-right',
        hideOnMobile && 'hidden sm:table-cell',
        className,
      )}
    >
      {children ?? <span className="text-muted-foreground">—</span>}
    </td>
  );
}

/**
 * The row's identity cell: the name that opens it, plus one line of context.
 * Always the first column, so every table has the same left edge.
 */
export function RecordTablePrimaryCell({
  title,
  subtitle,
  width = 280,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  width?: number;
}) {
  return (
    <RecordTableCell>
      <div className="min-w-0" style={{ maxWidth: width }}>
        <span className="block truncate font-medium text-foreground">{title}</span>
        {subtitle && (
          <span className="mt-0.5 block truncate text-xs text-muted-foreground">{subtitle}</span>
        )}
      </div>
    </RecordTableCell>
  );
}
